/**
 * Textura de fundo da conversa: um ladrilho SVG embutido no CSS como data URI.
 *
 * **Desenho autoral.** O padrão de rabiscos do WhatsApp é arte da Meta; este aqui é nosso, feito no
 * mesmo espírito — glifos de linha simples, esparsos, sem preenchimento. Nada foi extraído nem traçado
 * por cima do arquivo deles.
 *
 * Três decisões que o formato impõe:
 *
 * 1. **Data URI, não arquivo.** O widget é um `<script>` só, injetado em site de terceiro. Uma imagem
 *    externa seria mais uma requisição, poderia bater no CSP do site host e quebraria o widget em
 *    ambiente sem rede (preview do painel, teste).
 * 2. **Opacidade dentro do SVG**, via `stroke-opacity`. A alternativa seria uma camada `::before` com
 *    `opacity`, mas ela viveria sobre uma área com rolagem, exigindo `z-index` e brigando com as
 *    mensagens.
 * 3. **Codificação mínima, não base64.** Base64 cresce 33%, e `encodeURIComponent` cresceria ~40%
 *    codificando todo espaço e barra. Só o que o CSS e a URL realmente exigem é escapado.
 */

/** Lado do ladrilho. O CSS repete com `background-size` deste valor. */
const TILE = 320;

/**
 * Os glifos. Cada `d` é desenhado numa caixa local de ~24×24, e o `t` posiciona no ladrilho.
 *
 * Todos ficam a pelo menos 16px das bordas: glifo cortado na borda precisaria do complemento exato na
 * borda oposta para a emenda não aparecer. Com a faixa de silêncio, o ladrilho encaixa em qualquer
 * ordem — e a 5–6% de opacidade essa faixa é imperceptível.
 */
const GLYPHS: { d: string; t: string }[] = [
  // estrela
  { d: 'M12 3l2.6 5.6 6 .9-4.4 4.1 1.1 6-5.3-3-5.3 3 1.1-6L3.4 9.5l6-.9z', t: 'translate(22 28) scale(1.15)' },
  // balão de conversa
  { d: 'M3 5h18v12H11l-5 4v-4H3z', t: 'translate(98 20) rotate(-8) scale(1.25)' },
  // coração
  { d: 'M12 20C5 15 4 10 7 8c2-1.4 4 0 5 1.6C13 8 15 6.6 17 8c3 2 2 7-5 12z', t: 'translate(178 34) scale(1.1)' },
  // nota musical
  { d: 'M9 17V6l8-2v3l-8 2M9 17a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0', t: 'translate(252 24) rotate(10) scale(1.1)' },
  // caneca
  { d: 'M5 8h12v6a5 5 0 01-5 5h-2a5 5 0 01-5-5zM17 9h2a2 2 0 010 4h-2M4 21h14', t: 'translate(56 92) scale(1.15)' },
  // sol
  { d: 'M12 7a5 5 0 100 10 5 5 0 000-10M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3M4.6 4.6l2 2M17.4 17.4l2 2M19.4 4.6l-2 2M6.6 17.4l-2 2', t: 'translate(136 100) scale(1.05)' },
  // folha
  { d: 'M4 20C4 10 10 4 20 4c0 10-6 16-16 16M8 16c2-4 5-6 9-7', t: 'translate(214 86) rotate(-12) scale(1.2)' },
  // relógio
  { d: 'M12 3a9 9 0 100 18 9 9 0 000-18M12 8v5l3.5 2', t: 'translate(266 128) scale(1.05)' },
  // avião de papel
  { d: 'M2 12L22 3l-7 18-3-7zM12 14l10-11', t: 'translate(24 156) rotate(6) scale(1.25)' },
  // nuvem
  { d: 'M6 17h11a4 4 0 00.5-8A6 6 0 006 9a4 4 0 000 8', t: 'translate(104 170) scale(1.2)' },
  // presente
  { d: 'M3 9h18v11H3zM3 9l2-4h5l2 4M21 9l-2-4h-5l-2 4M12 9v11', t: 'translate(188 158) rotate(-6) scale(1.1)' },
  // lâmpada
  { d: 'M9.5 21h5M10 18h4M12 3a6 6 0 00-3 11v4h6v-4a6 6 0 00-3-11', t: 'translate(58 230) scale(1.2)' },
  // câmera
  { d: 'M3 8h4l2-2h6l2 2h4v11H3zM12 17a3.5 3.5 0 100-7 3.5 3.5 0 000 7', t: 'translate(138 242) rotate(8) scale(1.1)' },
  // guarda-chuva
  { d: 'M12 4a9 9 0 019 8H3a9 9 0 019-8M12 12v6a2.5 2.5 0 004 1.5', t: 'translate(220 224) scale(1.15)' },
  // espiral
  { d: 'M14 12a3 3 0 10-3 3c3 0 5-2 5-5a6 6 0 10-6 6', t: 'translate(266 262) scale(1.1)' },
];

/**
 * Escapa o mínimo para o SVG viver dentro de `url("…")` num CSS.
 *
 * A ordem importa: `%` primeiro, senão os `%` introduzidos pelas trocas seguintes seriam escapados de
 * novo, virando `%253C`. Aspas duplas não aparecem no nosso SVG (os atributos usam aspas simples), mas
 * a troca fica como rede de segurança para quem editar os glifos depois.
 */
function encodeSvg(svg: string): string {
  return svg
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/%/g, '%25')
    .replace(/#/g, '%23')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E')
    .replace(/"/g, '%22');
}

/** O ladrilho como XML, sem codificar. Exposto para os testes conferirem higiene do SVG. */
export function doodleSvg(color: string, opacity: number): string {
  const paths = GLYPHS.map((glyph) => `<path d='${glyph.d}' transform='${glyph.t}'/>`).join('');
  return (
    `<svg xmlns='http://www.w3.org/2000/svg' width='${TILE}' height='${TILE}' viewBox='0 0 ${TILE} ${TILE}'` +
    ` fill='none' stroke='${color}' stroke-opacity='${opacity}' stroke-width='1.6'` +
    ` stroke-linecap='round' stroke-linejoin='round'>${paths}</svg>`
  );
}

/** Pronto para entrar no `background-image`, com as aspas do `url()` incluídas. */
export function doodleDataUri(color: string, opacity: number): string {
  return `url("data:image/svg+xml,${encodeSvg(doodleSvg(color, opacity))}")`;
}

export const DOODLE_TILE_PX = TILE;
